import { Link } from "react-router";
import { SiteCopy, type SiteCopyKey } from "../copy";
import { CONTACT_HREF, ROUTES } from "../links";
import { HowMap } from "../widgets/HowMap";
import { SecFlow } from "../widgets/SecFlow";
import { usePageTitle } from "./usePageTitle";
import { PageFaq } from "../widgets/PageFaq";

const STEPS: { icon: string; b: string; p: string }[] = [
  {
    icon: '<path d="M12 3v4m0 10v4M3 12h4m10 0h4"/><circle cx="12" cy="12" r="4.4"/>',
    b: "Tell us what you&rsquo;re working toward",
    p: "Score what matters out of ten. Ties allowed.",
  },
  {
    icon: '<path d="M9.5 14.5 14.5 9.5"/><path d="M11 7.4 12.9 5.5a3.6 3.6 0 0 1 5.1 5.1l-1.9 1.9"/><path d="M13 16.6l-1.9 1.9a3.6 3.6 0 0 1-5.1-5.1l1.9-1.9"/>',
    b: "Connect your accounts",
    p: "Read-only, through Flinks and Wealthica. No typing.",
  },
  {
    icon: '<path d="M5 20V9a3 3 0 0 1 3-3h4"/><path d="M19 20V9a3 3 0 0 0-3-3h-4"/><path d="M12 3.2 12 6"/>',
    b: "Abi builds both plans",
    p: "Yours today, and yours reordered. Day by day to age 90.",
  },
  {
    icon: '<path d="M9 7h11M9 12h11M9 17h7"/><path d="M4.4 7h.01M4.4 12h.01M4.4 17h.01"/>',
    b: "Get the highest-impact actions",
    p: "A short list you can act on today, in order.",
  },
  {
    icon: '<path d="M3 12.5h3.6L9 7l3.2 10L15 12.5h6"/>',
    b: "Watch it update in real time",
    p: "Spending flows in daily. It tells you when things drift.",
  },
  {
    icon: '<path d="M9.3 9.2a2.8 2.8 0 1 1 3.6 2.7c-.6.2-.9.8-.9 1.4v.5"/><path d="M12 17.4h.01"/><circle cx="12" cy="12" r="8.6"/>',
    b: "Ask it anything",
    p: "Answered against your own numbers, not a generic calculator.",
  },
];

const SEC_ROWS = [1, 2, 3, 4, 5, 6].map((i) => ({
  h: `how.sec.a${i}h` as SiteCopyKey,
  p: `how.sec.a${i}p` as SiteCopyKey,
}));

/** How it works (the prototype's `data-s="how"`). */
export function HowItWorksPage() {
  usePageTitle("How it works");
  return (
    <div className="spage on" data-s="how">
      <header className="hero" style={{ paddingBottom: 0 }}>
        <div className="wrap">
          <h1>
            From a bank login to a <em>lifetime</em> plan, in about ten minutes.
          </h1>
          <p className="lede">
            Six checkpoints. Two of them are yours. Abi does the rest, and keeps doing it.
          </p>
          <div className="cta" style={{ marginTop: 26 }}>
            <Link className="sbtn p big" to={ROUTES.start}>
              Start your plan
            </Link>
            <a className="sbtn big" href={CONTACT_HREF}>
              Book a demo of Abi for your team
            </a>
          </div>
        </div>
      </header>
      <section className="band">
        <div className="wrap">
          <HowMap
            steps={STEPS.map((s) => (
              <>
                <span className="hsi">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: s.icon }}
                  />
                </span>
                <b dangerouslySetInnerHTML={{ __html: s.b }} />
                <p>{s.p}</p>
              </>
            ))}
          />
          <div style={{ textAlign: "center", marginTop: 44 }}>
            <Link className="sbtn p big" to={ROUTES.start}>
              Start your plan
            </Link>
          </div>
        </div>
      </section>

      {/* how the connection works */}
      <section className="band bWarm" id="howsec">
        <div className="wrap">
          <div className="shead">
            <SiteCopy as="h2" k="how.sec.h2" />
            <SiteCopy as="p" k="how.sec.p" />
          </div>
          <div className="stwide dark" style={{ padding: "32px 36px 22px" }}>
            <SecFlow id="secflow3" />
            <SiteCopy as="p" className="secflow-cap" k="how.sec.flowcap" />
          </div>
          <div className="secgrid">
            {SEC_ROWS.map((r) => (
              <div className="secrow" key={r.h}>
                <span className="ic">&#9679;</span>
                <div>
                  <SiteCopy as="b" k={r.h} />
                  <SiteCopy as="p" k={r.p} />
                </div>
              </div>
            ))}
          </div>
          <SiteCopy
            as="p"
            className="fcap"
            style={{ textAlign: "center", marginTop: 26 }}
            k="how.sec.note"
          />
        </div>
      </section>
      <PageFaq page="how" />
    </div>
  );
}
